import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DebouncedFunc, debounce } from 'lodash';
import { AutoComplete } from '@fullstory/components';

const DEBOUNCE_TIMEOUT = 250;

export interface URLRuleAutoCompleteProps {
  /** The current value of the input */
  value: string;
  /** Callback when the input is changed */
  onChange: (value: string) => void;
  /** Whether the current value is invalid */
  invalid: boolean;
  /** Callback to fetch autocomplete suggestions. This function will be debounced by the component. */
  getSuggestions: (value: string) => Promise<string[]>;
  /** Optional callback that happens on focus */
  onFocus?: () => void;
  /** Optional callback that happens on blur */
  onBlur?: () => void;
  placeholder?: string;
  testId?: string;
  autoFocusInput?: boolean;
}

export const URLRuleAutoComplete: FC<URLRuleAutoCompleteProps> = ({
  value,
  onChange,
  invalid,
  getSuggestions,
  onFocus: _onFocus,
  onBlur: _onBlur,
  placeholder,
  testId,
  autoFocusInput,
}) => {
  const [options, setOptions] = useState<{ id: string; label: string }[]>([]);

  const [focused, setFocused] = useState(false);
  const onFocus = useCallback(() => {
    setFocused(true);
    _onFocus?.();
  }, [_onFocus]);
  const onBlur = useCallback(() => {
    setFocused(false);
    _onBlur?.();
  }, [_onBlur]);

  const fetchSuggestions = useCallback(
    async (value: string) => {
      try {
        const suggestions = await getSuggestions(value);
        setOptions(suggestions.map(toOption));
      } catch (e) {
        console.error(`failed to fetch suggestions: ${e}`);
      }
    },
    [getSuggestions],
  );

  const debouncedFetchRef = useRef<DebouncedFunc<(value: string) => void>>();
  const debouncedFetch = useMemo(() => {
    if (debouncedFetchRef.current) {
      debouncedFetchRef.current.cancel();
    }
    const debounced = debounce(fetchSuggestions, DEBOUNCE_TIMEOUT);
    debouncedFetchRef.current = debounced;
    return debounced;
  }, [fetchSuggestions]);

  // When focused and debouncedFetch / value changes, we want to fetch new suggestions.
  useEffect(() => {
    if (focused) {
      debouncedFetch(value);
    }
  }, [focused, debouncedFetch, value]);

  // Selected autocomplete suggestion
  const selected = useMemo(() => toOption(value), [value]);

  // Update input and trigger callback when a suggestion is selected
  const onSelect = useCallback(
    (value?: { label: string }) => {
      if (value) {
        onChange(value.label);
      }
    },
    [onChange],
  );

  return (
    <AutoComplete
      inputValue={value}
      onInputChange={onChange}
      options={options}
      onSelect={onSelect}
      selected={selected}
      inputInvalid={invalid}
      placeholder={placeholder}
      hideNoResults
      maxHeight={500}
      testId={testId}
      onFocus={onFocus}
      onInputBlur={onBlur}
      popoverWidth={640}
      autoFocusInput={autoFocusInput}
    />
  );
};

const toOption = (s: string) => ({ id: s, label: s });
